import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import Modal from "../../App/Components/Modal/Modal";
import { MessageFrequencyTypes } from "../../App/Enums/Message";
import { useUpsertCampaignReport } from "../../App/Queries/CampaignQueries";
import { validateEmail } from "../../App/Scripts/emailValidator";
import { useNotification } from "../../App/State/Hooks/notificationHooks";
import { useDebouncedHandler } from "../../App/State/Hooks/useDebouncedHandler";
import { ICampaign, IUpsertCampaignReportMessage } from "../../App/Typings/Campaigns";

export interface CampaignNotificationsModalProps {
  isOpen: boolean;
  handleModalClose: () => void;
  campaign: ICampaign;
}

interface ModalErrorState {
  hasError: boolean;
  errorMessage: string;
  errorType: "warning" | "error";
}

const noEmailsWarningMessage = "You do not have any email addresses entered.";
const invalidEmailMessage = "One or more invalid email addresses.";

export default function CampaignNotificationsModal({
  isOpen,
  handleModalClose,
  campaign,
}: CampaignNotificationsModalProps) {
  const { mutate: saveCampaignReport, isLoading } = useUpsertCampaignReport();
  const notification = useNotification();
  const [campaignReportSettings, setCampaignReportSettings] = useState<IUpsertCampaignReportMessage>({
    campaignId: campaign.id,
    deliveryMethod: campaign.campaignReportMessage?.messageRecurrance.deliveryMethod ?? 0,
    frequencyType: campaign.campaignReportMessage?.messageRecurrance.frequency ?? MessageFrequencyTypes.None,
    sendTo: campaign.campaignReportMessage?.messageRecurrance.sendTo ?? [],
    startDate: campaign.campaignReportMessage?.messageRecurrance.startDate ?? new Date(),
    timing: campaign.campaignReportMessage?.messageRecurrance.timing ?? 1,
  } as IUpsertCampaignReportMessage);
  const [errorState, setErrorState] = useState<ModalErrorState>({
    hasError: false,
    errorMessage: "",
    errorType: "error",
  });
  const theme = useTheme();

  const handleSaveCampaignReportClick = useDebouncedHandler(() => {
    if (isLoading) {
      return;
    }
    if (campaignReportSettings) {
      if (validateEmails(campaignReportSettings.sendTo)) {
        saveCampaignReport(campaignReportSettings);
        notification.displaySuccess("Form notification settings updated.");
        handleModalClose();
      } else {
        notification.displayError(invalidEmailMessage);
      }
    } else {
      notification.displayError("Something went wrong, please check your options and try again.");
    }
  }, 300);

  const validateEmails = (emails: string[]) => {
    let valid = true;

    emails.forEach((c) => {
      if (!validateEmail(c)) {
        valid = false;
      }
    });

    return valid;
  };

  const debouncedHandleEmailInput = useDebouncedHandler(
    (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (!validateEmail(ev.target.value.trim())) {
        setErrorState({
          hasError: true,
          errorMessage: invalidEmailMessage,
          errorType: "error",
        });
      } else {
        setErrorState({
          hasError: false,
          errorMessage: "",
          errorType: "error",
        });
      }
    },
    300,
  );

  const handleEmailsChange = (_ev: React.SyntheticEvent<Element, Event>, value: string[] | undefined) => {
    setCampaignReportSettings((campaignReportSettings) => ({
      ...campaignReportSettings,
      sendTo: value ?? [],
    }));
  };

  const handleBlurInput = (_ev: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
    const inputValue = _ev.target.value.trim();
    if (inputValue) {
      const enterEvent = new KeyboardEvent("keydown", {
        key: "Enter",
        code: "Enter",
        keyCode: 13,
        bubbles: true,
      });
      _ev.target.dispatchEvent(enterEvent);
    }
  };

  const handleFrequencyChange = (_ev: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setCampaignReportSettings((campaignReportSettings) => ({
      ...campaignReportSettings,
      frequencyType: Number(value) as MessageFrequencyTypes,
    }));
  };

  const handleStartDateChange = (date: Date | null) => {
    setCampaignReportSettings((campaignReportSettings) => ({
      ...campaignReportSettings,
      startDate: date ?? new Date(),
    }));
  };

  const handleTimingChange = (ev: SelectChangeEvent<number>) => {
    setCampaignReportSettings((campaignReportSettings) => ({
      ...campaignReportSettings,
      timing: Number(ev.target.value),
    }));
  };

  const getTimingLabel = (freq: MessageFrequencyTypes, plural?: boolean) => {
    switch (freq) {
      case MessageFrequencyTypes.Daily:
        return "day" + (plural ? "s" : "");
      case MessageFrequencyTypes.Weekly:
        return "week" + (plural ? "s" : "");
      case MessageFrequencyTypes.Monthly:
        return "month" + (plural ? "s" : "");
    }

    return "Immediate";
  };

  const getSendingMessage = () => {
    if (campaignReportSettings.frequencyType === MessageFrequencyTypes.None) {
      return "Receive an email for every form submission immedidately.";
    }

    const timingStr =
      campaignReportSettings.timing > 1
        ? `${campaignReportSettings.timing} ${getTimingLabel(campaignReportSettings.frequencyType, true)}`
        : getTimingLabel(campaignReportSettings.frequencyType, false);
    const dateStr = dayjs(campaignReportSettings.startDate).format("MMMM DD, YYYY");

    return `Receive an email every ${timingStr} starting ${dateStr}.`;
  };

  useEffect(() => {
    if (campaignReportSettings.sendTo.length <= 0) {
      setErrorState({
        hasError: true,
        errorMessage: noEmailsWarningMessage,
        errorType: "warning",
      });
    } else if (!validateEmails(campaignReportSettings.sendTo)) {
      setErrorState({
        hasError: true,
        errorMessage: invalidEmailMessage,
        errorType: "error",
      });
    } else {
      setErrorState({
        hasError: false,
        errorMessage: "",
        errorType: "error",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(campaignReportSettings.sendTo)]);

  return (
    <Modal
      dataTestId="campaign-notifications-modal"
      content={
        <>
          <Box sx={{ display: "flex", mb: 2, flexDirection: "column" }}>
            <InputLabel
              id="campaign-notification-emails-label"
              htmlFor="campaign-notification-emails"
              sx={{ fontWeight: "bold", mb: 0.5 }}
            >
              Email Address(es)
            </InputLabel>
            <Autocomplete
              multiple
              id="campaign-notification-emails"
              options={[]}
              value={[...campaignReportSettings.sendTo]}
              onChange={handleEmailsChange}
              freeSolo
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip
                    color={validateEmail(option) ? "primary" : "error"}
                    variant="filled"
                    label={option}
                    {...getTagProps({ index })}
                    key={option + index}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  onChange={debouncedHandleEmailInput}
                  onBlur={handleBlurInput}
                  placeholder={
                    campaignReportSettings.sendTo.length <= 0
                      ? "johndoe@email.com (press enter or tab to submit)"
                      : ""
                  }
                />
              )}
            />
          </Box>
          <Box sx={{ display: "flex", mb: 2, flexDirection: "column" }}>
            <InputLabel
              id="campaign-notificiation-frequency-label"
              htmlFor="campaign-notificiation-frequency"
              sx={{ fontWeight: "bold", mb: 0.5 }}
            >
              Recurrence
            </InputLabel>
            <RadioGroup
              row
              aria-labelledby="campaign-notificiation-frequency-label"
              name="campaign-notificiation-frequency"
              defaultValue={campaignReportSettings.frequencyType}
              onChange={handleFrequencyChange}
            >
              <FormControlLabel value="0" control={<Radio />} label="Immediate" />
              <FormControlLabel value="1" control={<Radio />} label="Daily" />
              <FormControlLabel value="2" control={<Radio />} label="Weekly" />
              <FormControlLabel value="3" control={<Radio />} label="Monthly" />
            </RadioGroup>
          </Box>
          <Box sx={{ display: "flex", mb: 2, flexDirection: "column" }}>
            <Stack direction="row">
              <Box>
                <InputLabel
                  id="campaign-notificiation-start-date-label"
                  htmlFor="campaign-notificiation-start-date"
                  sx={{ fontWeight: "bold", mb: 0.5 }}
                >
                  Start Date
                </InputLabel>
                <DatePicker
                  value={campaignReportSettings.startDate}
                  InputProps={{ id: "start-date" }}
                  renderInput={(props) => <TextField {...props} placeholder="Start Date" />}
                  onChange={handleStartDateChange}
                  disabled={campaignReportSettings.frequencyType === MessageFrequencyTypes.None}
                />
              </Box>
              <Box sx={{ ml: 5 }}>
                <InputLabel id="timing-select-label" sx={{ fontWeight: "bold", mb: 0.5 }}>
                  Frequency ({getTimingLabel(campaignReportSettings.frequencyType)})
                </InputLabel>
                <Select
                  labelId="timing-select-label"
                  id="timing-select"
                  onChange={handleTimingChange}
                  defaultValue={campaignReportSettings.timing}
                  disabled={campaignReportSettings.frequencyType === MessageFrequencyTypes.None}
                >
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={2}>2</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                  <MenuItem value={4}>4</MenuItem>
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={6}>6</MenuItem>
                </Select>
              </Box>
            </Stack>
          </Box>
          <Box sx={{ mt: 1, mb: 2 }}>
            <Typography>{getSendingMessage()}</Typography>
          </Box>
          <Divider />
        </>
      }
      handleCloseModal={handleModalClose}
      isOpen={isOpen}
      title="Form Completion Notifications"
      size="md"
      footerContent={
        <>
          {errorState.hasError && (
            <Typography
              sx={{ mr: 3 }}
              color={errorState.errorType === "error" ? theme.palette.error.main : theme.palette.warning.main}
            >
              {errorState.errorMessage}
            </Typography>
          )}
          <Button variant="contained" color="inherit" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveCampaignReportClick}
            disabled={
              (campaignReportSettings.sendTo.length > 0 && !validateEmails(campaignReportSettings.sendTo)) ||
              (errorState.hasError && errorState.errorType === "error") ||
              isLoading
            }
          >
            Save
          </Button>
        </>
      }
    />
  );
}
